import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Loader2, CreditCard, Calendar, User, FileText, Hash, Banknote } from 'lucide-react'
import { SimpleAccountSelector } from '@/app/components/common/SimpleAccountSelector'
import { VoucherResponse } from '@/app/types/voucher'
import { format, parseISO } from 'date-fns'
import { toast } from 'sonner'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { refundCreditNote } from '@/app/services/paymentService'
import { CreditNoteRefundCreate } from '@/app/types/payment'
import { useAccountFilters } from '@/app/hooks/useAccountFilters'

interface RefundDialogProps {
  isOpen: boolean
  onClose: () => void
  creditNote: VoucherResponse
}

// Helper function to format currency
const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return 'N/A'
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR'
  }).format(Number(amount))
}

// Helper function to format date
const formatDate = (dateString: string | Date | undefined | null): string => {
  if (!dateString) return 'N/A'
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : dateString
    return format(date, 'dd MMM yyyy')
  } catch (error) {
    return String(dateString)
  }
}

// Detail item component
interface DetailItemProps {
  icon: React.ElementType
  label: string
  value: React.ReactNode
  className?: string
}

const DetailItem: React.FC<DetailItemProps> = ({ icon: Icon, label, value, className }) => (
  <div className={`flex items-start space-x-2 ${className}`}>
    <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
    <div className="min-w-0 flex-1">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-sm font-medium break-words">{value}</p>
    </div>
  </div>
)

export const RefundDialog: React.FC<RefundDialogProps> = ({
  isOpen,
  onClose,
  creditNote
}) => {
  const queryClient = useQueryClient()
  const [paidFromAccountId, setPaidFromAccountId] = useState<number | null>(null)

  // Get account filters based on user's default site
  const { paymentAccountIds } = useAccountFilters()

  // Use the credit note's total amount as the refund amount
  const refundAmount = creditNote.total_amount ?? 0
  // Check payment status from VoucherResponse fields
  const creditNoteStatus = (creditNote as any).status || 'Draft'

  // Create refund mutation
  const createRefundMutation = useMutation({
    mutationFn: (data: CreditNoteRefundCreate) => refundCreditNote(data),
    onSuccess: () => {
      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['creditNotes'] })
      queryClient.invalidateQueries({ queryKey: ['creditNote', creditNote.id] })
      queryClient.invalidateQueries({ queryKey: ['payments-made'] })
      queryClient.invalidateQueries({ queryKey: ['party-statement', creditNote.party_id] })

      toast.success(`Refund processed successfully for Credit Note #${creditNote.voucher_number}`)
      onClose()
    },
    onError: (error: Error) => {
      toast.error(`Failed to process refund: ${error.message}`)
    }
  })

  const handleConfirmRefund = () => {
    if (!paidFromAccountId) {
      toast.error('Please select a payment account')
      return
    }

    if (refundAmount <= 0) {
      toast.error('Credit note has no amount to refund')
      return
    }

    if (!creditNote.party_id) {
      toast.error('Credit note has no associated party')
      return
    }

    // Prepare refund data
    const refundData: CreditNoteRefundCreate = {
      party_id: creditNote.party_id,
      paid_from_account_id: paidFromAccountId,
      total_payment_amount: refundAmount,
      transaction_date: format(new Date(), 'yyyy-MM-dd'),
      allocations: [
        {
          credit_note_id: creditNote.id,
          amount_to_allocate: refundAmount
        }
      ],
      reference_number: `Refund for ${creditNote.voucher_number}`,
      narration: `Full refund for Credit Note #${creditNote.voucher_number}`,
      site_id: creditNote.site_id || undefined
    }

    createRefundMutation.mutate(refundData)
  }

  const isSubmitting = createRefundMutation.isPending

  // Check if credit note is already paid/refunded
  const isAlreadyRefunded = creditNoteStatus === 'Paid' || (creditNote as any).payment_status === 'Paid'

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Process Credit Note Refund</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1">
          {/* Credit Note Details */}
          <div className="bg-muted/50 p-4 rounded-lg space-y-3">
            <h4 className="font-medium text-sm">Credit Note Details</h4>
            <div className="grid grid-cols-2 gap-4">
              <DetailItem icon={Hash} label="Credit Note #" value={creditNote.voucher_number} />
              <DetailItem
                icon={Calendar}
                label="Date"
                value={formatDate(creditNote.transaction_date)}
              />
              <DetailItem icon={User} label="Customer" value={creditNote.party?.name || 'N/A'} />
              <DetailItem icon={FileText} label="Status" value={creditNoteStatus} />
            </div>
            <div className="pt-2 border-t">
              <DetailItem
                icon={Banknote}
                label="Refund Amount"
                value={formatCurrency(refundAmount)}
                className="text-lg"
              />
            </div>
          </div>

          {/* Payment Account Selection */}
          <div className="space-y-2">
            <Label htmlFor="payment-account">
              Payment From Account <span className="text-destructive">*</span>
            </Label>
            <SimpleAccountSelector
              value={paidFromAccountId}
              onChange={setPaidFromAccountId}
              placeholder="Select bank or cash account..."
              filterAccountIds={paymentAccountIds}
              disabled={isSubmitting || isAlreadyRefunded}
            />
            <p className="text-xs text-muted-foreground">
              Select the bank or cash account from which the refund will be paid
            </p>
          </div>

          {isAlreadyRefunded && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-md">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                This credit note has already been refunded or paid.
              </p>
            </div>
          )}

          {refundAmount <= 0 && !isAlreadyRefunded && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-md">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                This credit note has no amount to refund.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRefund}
            disabled={isSubmitting || !paidFromAccountId || refundAmount <= 0 || isAlreadyRefunded}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing Refund...
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-4 w-4" />
                Confirm Refund
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}